import { useEffect, useRef, useState } from "react";

export default function PinDialog({
  title,
  detail,
  onConfirm,
  onCancel,
  confirmLabel = "Delete",
  confirmBusyLabel = "Deleting…",
  confirmClass = "bg-danger text-white",
}: {
  title: string;
  detail: string;
  onConfirm: (pin: string) => Promise<void>;
  onCancel: () => void;
  confirmLabel?: string;
  confirmBusyLabel?: string;
  confirmClass?: string;
}) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async () => {
    if (pin.length !== 4 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(pin);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
      setPin("");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-6"
      onClick={onCancel}
    >
      <div
        className="pop-in flex w-full max-w-xs flex-col gap-4 rounded-[20px] bg-white p-6 shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-[19px] font-bold tracking-[-0.02em]">{title}</h2>
        <p className="text-sm font-semibold text-ink/60">{detail}</p>
        <input
          ref={inputRef}
          autoFocus
          type="text"
          inputMode="numeric"
          placeholder="Enter PIN"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className={`h-14 w-full rounded-[14px] bg-white text-center outline-none placeholder:text-ink/40 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20 ${
            pin ? "text-3xl font-bold tracking-[0.4em]" : "text-base font-semibold"
          }`}
        />
        {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="h-12 flex-1 rounded-[14px] bg-white font-bold border border-line text-[#3a1c25]"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={pin.length !== 4 || busy}
            className={`h-12 flex-1 rounded-[14px] font-bold disabled:opacity-50 ${confirmClass}`}
          >
            {busy ? confirmBusyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
